/**
 * useTourTelemetry — KAN-3056 Phase 2
 * ===================================
 *
 * Wraps the canonical `captureEvent()` helper from `src/posthog.ts` and
 * surfaces six tightly-typed firing functions so Phase 3's driver.js
 * callbacks can't drift from the registry in `src/lib/tourEvents.ts`.
 *
 * Pattern mirrors `useLoadingTimeout`:
 *   - `tier` and `auth_state` are pulled from `useAuth()`;
 *   - `route` from `useLocation().pathname`;
 *   - both are auto-attached, callers only pass tour-specific props.
 *
 * Telemetry must NEVER throw into render — every emit is wrapped in
 * try/catch and degrades to a no-op if PostHog is gated by consent or
 * the user is anonymous (free tier with no auth_user_id still fires; we
 * just record auth_state='anon' which is correct behaviour).
 *
 * Phase 3 (KAN-3057) is the consumer. Phase 2 ships the hook + a dev
 * verification script (`scripts/verify-tour-telemetry.ts`) that fires
 * each event into the dev PostHog project for the acceptance check.
 */

import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { captureEvent } from '../posthog';
import {
  TOUR_EVENTS,
  type TourCompletedProps,
  type TourReplayClickedProps,
  type TourStartedProps,
  type TourStepEventProps,
} from '../lib/tourEvents';

type CommonContext = {
  auth_state: 'anon' | 'authed';
  tier: string | undefined;
  route: string;
};

function emit(event: string, props: Record<string, unknown>, ctx: CommonContext) {
  const payload: Record<string, unknown> = {
    auth_state: ctx.auth_state,
    route: ctx.route,
    ...props,
  };
  if (ctx.tier) payload.tier = ctx.tier;

  try {
    captureEvent(event, payload);
  } catch {
    /* no-op — telemetry must never throw into render */
  }
}

export interface UseTourTelemetryReturn {
  trackTourStarted: (props: TourStartedProps) => void;
  trackTourStepCompleted: (props: TourStepEventProps) => void;
  trackTourStepBack: (props: TourStepEventProps) => void;
  trackTourSkipped: (props: TourStepEventProps) => void;
  trackTourCompleted: (props: TourCompletedProps) => void;
  trackTourReplayClicked: (props: TourReplayClickedProps) => void;
}

export function useTourTelemetry(): UseTourTelemetryReturn {
  const location = useLocation();
  const { user } = useAuth();

  const ctx: CommonContext = {
    auth_state: user?.auth_user_id ? 'authed' : 'anon',
    tier: user?.subscription_tier,
    route: location.pathname,
  };

  const trackTourStarted = useCallback(
    (props: TourStartedProps) => emit(TOUR_EVENTS.STARTED, props, ctx),
    // ctx is rebuilt every render but each field is stable per render — emit reads
    // it inline so we don't need it in the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx.auth_state, ctx.tier, ctx.route],
  );

  const trackTourStepCompleted = useCallback(
    (props: TourStepEventProps) => emit(TOUR_EVENTS.STEP_COMPLETED, props, ctx),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx.auth_state, ctx.tier, ctx.route],
  );

  const trackTourStepBack = useCallback(
    (props: TourStepEventProps) => emit(TOUR_EVENTS.STEP_BACK, props, ctx),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx.auth_state, ctx.tier, ctx.route],
  );

  const trackTourSkipped = useCallback(
    (props: TourStepEventProps) => emit(TOUR_EVENTS.SKIPPED, props, ctx),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx.auth_state, ctx.tier, ctx.route],
  );

  const trackTourCompleted = useCallback(
    (props: TourCompletedProps) => emit(TOUR_EVENTS.COMPLETED, props, ctx),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx.auth_state, ctx.tier, ctx.route],
  );

  const trackTourReplayClicked = useCallback(
    (props: TourReplayClickedProps) => emit(TOUR_EVENTS.REPLAY_CLICKED, props, ctx),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx.auth_state, ctx.tier, ctx.route],
  );

  return {
    trackTourStarted,
    trackTourStepCompleted,
    trackTourStepBack,
    trackTourSkipped,
    trackTourCompleted,
    trackTourReplayClicked,
  };
}

export default useTourTelemetry;
